/**
 * Decides when to show the "assistant is working" indicator — the affordance
 * that bridges every gap where the agent is busy but nothing visible is
 * progressing: the initial wait before the first token, the pauses between tool
 * steps, and post-reasoning thinking. Mirrors the web app's
 * `shouldShowWaitingIndicator` so the two chats behave identically.
 */ import { UiDataPartType } from "@juristic/types/stream/ai/data_events";
import { ToolHookEventType } from "@juristic/types/stream/ai/tool_hooks";
import { WORD_EXEC } from "@Utils/doc/wordExec";
import { isToolUIPart } from "ai";
import { useWordExecCards } from "../../hooks/clientTools/wordExec";
import { findCardInterruption } from "./cardInterruption";
import { isCompacting, isReadingAttachments } from "./compaction";
/** Text/reasoning that is actively streaming — its own content is the activity. */ function isActivelyStreamingText(part) {
    return (part.type === "text" || part.type === "reasoning") && part.state === "streaming";
}
/** A tool call still running — its status pill shows its own spinner. */ function isVisibleRunningTool(part) {
    return isToolUIPart(part) && (part.state === "input-streaming" || part.state === "input-available");
}
/** The answer has landed: the stream is only closing, so there is nothing left to wait for. */ function endsWithFinishedText(parts) {
    const lastContent = [
        ...parts
    ].reverse().find((part)=>part.type === "text" || part.type === "reasoning" || part.type === "step-start" || isToolUIPart(part));
    return lastContent?.type === "text" && lastContent.state === "done";
}
const REQUEST_TYPES = new Set([
    ToolHookEventType.ConfirmationRequest,
    ToolHookEventType.ChoiceRequest
]);
const RESOLVED_TYPES = new Set([
    ToolHookEventType.ConfirmationResolved,
    ToolHookEventType.ChoiceResolved,
    ToolHookEventType.ClientToolResolved
]);
export function isWordExecWrite(data) {
    return data.type === ToolHookEventType.ClientToolRequest && data.toolName === WORD_EXEC && data.input.mode === "write";
}
function pendingRequests(parts) {
    const resolved = new Set();
    parts.forEach((part)=>{
        if (part.type === UiDataPartType.ToolHook && RESOLVED_TYPES.has(part.data.type)) {
            resolved.add(part.data.toolCallId);
        }
    });
    return parts.filter((part)=>part.type === UiDataPartType.ToolHook && (REQUEST_TYPES.has(part.data.type) || isWordExecWrite(part.data) && useWordExecCards.getState().cards[part.data.toolCallId]?.status === "waiting") && !resolved.has(part.data.toolCallId));
}
/** A request card (confirmation, edit proposal or question) still awaiting the user owns the screen. */ export function hasPendingHookCard(parts) {
    return pendingRequests(parts).length > 0;
}
/** The question docked above the composer: the first one still open in the newest message. */ export function findDockedChoice(messages, answered = {}) {
    const newest = messages[messages.length - 1];
    if (newest?.role !== "assistant") return undefined;
    return pendingRequests(newest.parts).map((part)=>part.data).find((data)=>data.type === ToolHookEventType.ChoiceRequest && !(data.toolCallId in answered) && !findCardInterruption(newest, data.toolCallId));
}
/**
 * True when the agent is responding and no specific live surface (streaming
 * text/reasoning, a running tool, or a pending request card) is on screen —
 * i.e. the gap the waiting indicator should fill.
 */ export function shouldShowWaitingIndicator(messages, isResponding) {
    if (!isResponding || messages.length === 0) return false;
    const last = messages[messages.length - 1];
    // Just sent, or the assistant turn hasn't produced anything yet.
    if (last.role !== "assistant" || !last.parts?.length) return true;
    // A pending card is the surface — let it own the screen.
    if (hasPendingHookCard(last.parts)) return false;
    if (isCompacting(last.parts) || isReadingAttachments(last.parts)) return false;
    const hasActiveSurface = last.parts.some((part)=>isActivelyStreamingText(part) || isVisibleRunningTool(part));
    return !hasActiveSurface && !endsWithFinishedText(last.parts);
}
